import { useEffect, useRef, useState } from 'react';
import { Button, Description, Drawer, type OverlayTriggerState } from '@heroui/react';
import {
  drawerFooterClassName,
  fullScreenDrawerDialogClassName,
} from '../ui/side-drawer-classes';
import { FloorPlanMarker } from './floor-plan-marker';
import { FLOOR_PLAN_IMAGE_URL, type FloorPlanRect } from './floor-plan-utils';

interface FloorPlanDrawerProps {
  state: OverlayTriggerState;
  title: string;
  subtitle?: string;
  rect: FloorPlanRect | null;
  editable?: boolean;
  onConfirm?: (rect: FloorPlanRect | null) => void;
}

export function FloorPlanDrawer({
  state,
  title,
  subtitle,
  rect,
  editable = false,
  onConfirm,
}: FloorPlanDrawerProps) {
  const [draft, setDraft] = useState<FloorPlanRect | null>(rect);
  const rectRef = useRef(rect);
  rectRef.current = rect;

  useEffect(() => {
    if (!state.isOpen) return;
    setDraft(rectRef.current);
  }, [state.isOpen]);

  const handleConfirm = () => {
    onConfirm?.(draft);
    state.close();
  };

  return (
    <Drawer state={state}>
      <Drawer.Backdrop isDismissable>
        <Drawer.Content placement="right">
          <Drawer.Dialog className={fullScreenDrawerDialogClassName}>
            <Drawer.Header>
              <Drawer.Heading>{title}</Drawer.Heading>
              <Drawer.CloseTrigger />
            </Drawer.Header>
            <Drawer.Body className="flex min-h-0 flex-1 flex-col overflow-hidden p-0">
              {subtitle ? (
                <Description className="shrink-0 px-6 py-2">{subtitle}</Description>
              ) : null}
              {state.isOpen ? (
                <FloorPlanMarker
                  key={`${rect?.x ?? 'none'}-${rect?.y ?? 'none'}-${editable ? 'edit' : 'view'}`}
                  imageUrl={FLOOR_PLAN_IMAGE_URL}
                  rect={editable ? draft : rect}
                  onChange={editable ? setDraft : undefined}
                  autoFocus
                  className="h-full"
                />
              ) : null}
            </Drawer.Body>
            <Drawer.Footer className={drawerFooterClassName}>
              {editable ? (
                <>
                  <Button variant="ghost" className="flex-1" onPress={state.close}>
                    Cancelar
                  </Button>
                  <Button variant="primary" className="flex-1" onPress={handleConfirm}>
                    Confirmar marcação
                  </Button>
                </>
              ) : (
                <Button variant="primary" className="w-full" onPress={state.close}>
                  Fechar
                </Button>
              )}
            </Drawer.Footer>
          </Drawer.Dialog>
        </Drawer.Content>
      </Drawer.Backdrop>
    </Drawer>
  );
}
